import { addDays, isRealTimestamp } from '~/utils/date';

import type {
  DeliveryRunDetail,
  ProductsUnavailable,
  ProductsUnavailableReason,
} from '../types/delivery-runs.types';

import { humanizeEnum } from './format-enum';

/** Per-product audit retention (integrations ClickHouse TTL) — the server's
 *  `retentionDays` wins when it sends one. */
export const DELIVERY_AUDIT_RETENTION_DAYS = 90;

/**
 * Everything a "no product rows" state card renders, resolved from the server's
 * answer plus the run's own fields. `derived` = the reason was inferred here from
 * the run (the server said nothing, or named a reason the run contradicts).
 */
export interface ResolvedProductsUnavailable {
  reason: ProductsUnavailableReason;
  derived: boolean;
  runStartedAt: string | null;
  retentionDays: number;
  /** When the rows were removed — server value, else start + retention. */
  expiredAt: string | null;
  /** Human label of the last step the run completed before failing, when known. */
  lastSuccessfulStep: string | null;
  errorCode: string | null;
  errorMessage: string | null;
}

/** Run fields the resolver reads — a subset so ledgers holding only part of a run can call it. */
type RunFacts = Pick<
  DeliveryRunDetail,
  'isTerminal' | 'phase' | 'totals' | 'errorCode' | 'errorMessage' | 'startedAt' | 'createdAt'
> &
  Partial<Pick<DeliveryRunDetail, 'stages'>>;

/** Products the run actually handled: the disjoint buckets, `accounted` when published. */
function processedCount(run: RunFacts): number {
  const t = run.totals;
  if (typeof t.accounted === 'number') return t.accounted;
  const skipped = t.skipped ?? (t.skippedUnchanged ?? 0) + (t.validationFailed ?? 0);
  return t.delivered + t.failed + skipped;
}

/**
 * `true` when a finished, unsuccessful run handled zero products — it failed
 * before it sent any (e.g. an auth rejection at the export trigger), so there are
 * no rows to show, whatever older servers claim ("not tracked" / "aged out").
 * CANCELLED is not a failure (same rule as integrations): a cancelled run with no
 * products keeps the server's RETENTION_EXPIRED / NOT_TRACKED answer.
 */
export function runFailedBeforeProducts(run: RunFacts | null | undefined): boolean {
  if (!run?.isTerminal) return false;
  if (run.phase === 'CANCELLED' || run.phase === 'COMPLETED') return false;
  const unsuccessful = run.phase === 'FAILED' || run.errorCode !== null;
  return unsuccessful && processedCount(run) === 0;
}

/**
 * Readable last-successful-step name: the server's step id mapped to the run's own
 * stage label (else humanised); with no server value, the last COMPLETED stage
 * before the run's first FAILED one (`null` when it failed at the first step).
 */
function lastSuccessfulStepLabel(
  step: string | null | undefined,
  run: RunFacts | null | undefined,
): string | null {
  const stages = run?.stages ?? [];
  if (step) {
    const known = stages.find((s) => s.name === step);
    if (known) return known.label;
    return /^[a-z0-9_]+$/i.test(step) && step.includes('_') ? humanizeEnum(step) : step;
  }
  const failedAt = stages.findIndex((s) => s.status === 'FAILED');
  if (failedAt <= 0) return null;
  const done = stages.slice(0, failedAt).filter((s) => s.status === 'COMPLETED');
  return done.at(-1)?.label ?? null;
}

/**
 * Resolve why a run's product ledger is empty. Server reason first (structured
 * block or legacy string), overridden to `FAILED_BEFORE_PRODUCTS` when the run
 * itself shows it failed having processed nothing; with no server reason that
 * same inference applies only on an `unfiltered` view (a filtered empty page is
 * just "nothing matches"). `null` = render the caller's plain empty state.
 */
export function resolveProductsUnavailable(
  server: ProductsUnavailable | null | undefined,
  run: RunFacts | null | undefined,
  { unfiltered }: { unfiltered: boolean },
): ResolvedProductsUnavailable | null {
  const failedEarly = runFailedBeforeProducts(run);
  let reason: ProductsUnavailableReason | null = server?.reason ?? null;
  let derived = false;
  // A run that superseded every product is explained as such, never as an early failure.
  if (
    failedEarly &&
    reason !== 'FAILED_BEFORE_PRODUCTS' &&
    reason !== 'ALL_SUPERSEDED' &&
    (reason !== null || unfiltered)
  ) {
    reason = 'FAILED_BEFORE_PRODUCTS';
    derived = true;
  }
  if (!reason) return null;

  // First real instant: the server's, else when the run started, else when it was queued.
  const runStartedAt =
    [server?.runStartedAt, run?.startedAt, run?.createdAt].find(isRealTimestamp) ?? null;
  const retentionDays =
    typeof server?.retentionDays === 'number' && server.retentionDays > 0
      ? server.retentionDays
      : DELIVERY_AUDIT_RETENTION_DAYS;
  const serverExpiredAt = isRealTimestamp(server?.expiredAt) ? server.expiredAt : null;
  const expiredAt =
    reason === 'RETENTION_EXPIRED'
      ? (serverExpiredAt ?? (runStartedAt ? addDays(runStartedAt, retentionDays) : null))
      : null;

  return {
    reason,
    derived,
    runStartedAt,
    retentionDays,
    expiredAt,
    lastSuccessfulStep:
      reason === 'FAILED_BEFORE_PRODUCTS'
        ? lastSuccessfulStepLabel(server?.lastSuccessfulStep, run)
        : null,
    errorCode: server?.errorCode ?? run?.errorCode ?? null,
    errorMessage: run?.errorMessage ?? null,
  };
}
